import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  Check,
  CompareArrows,
  ContentCopy,
  Delete,
  Edit,
  Info,
} from '@mui/icons-material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';

import Table from '../../shared/Table';
import OverflowTooltip from '../../shared/OverflowTooltip';
import * as API from '../../../services/API';

import {
  ALL_MEMBERS_ATTRIBUTE,
  ALL_MEMBERS_RULE,
  blankConnection,
  EXACT_MATCH_RULE,
  generateConnectionId,
  isAllMembersAdjustment,
  IDimensionOption,
  IDimMappingAdjustment,
  IIMConnection,
  IModelInputOption,
  IModelOption,
  ISourceModelInstanceOption,
} from '../ManageInterModelSubscriptions';

const TAG_NAME_MAX_LENGTH = 20;
const CONNECTION_NAME_MAX_LENGTH = 25;

// Sum of every connectionColumns minWidth below. Enforced as the table's
// own minWidth so it scrolls horizontally on a narrow dialog instead of
// squeezing/cropping columns.
const CONNECTIONS_TABLE_MIN_WIDTH = 1030;

// Sentinel value for a Dim Mapping Adjustment side with no specific
// ClientAttribute - tells the backend to match on the dimension itself.
const DIMENSION_NAME_ATTRIBUTE = 'Name';

// Case-insensitive check for the Name attribute to prevent duplicates
// when a real ClientAttribute is literally named "name" or "NAME".
const isNameAttribute = (a?: string): boolean =>
  !!a && a.toLowerCase() === DIMENSION_NAME_ATTRIBUTE.toLowerCase();

// Shared dense sizing for text/select inputs on this screen - shrinks the
// input's own vertical padding below what MUI's `size="small"` gives.
const DENSE_FIELD_SX = {
  '& .MuiInputBase-input': { paddingTop: '6px', paddingBottom: '6px' },
  '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px' },
};

// Caps every dropdown's open list to ~6-7 rows instead of growing to fit.
const SELECT_ITEM_HEIGHT = 36;
const SELECT_MENU_PROPS = {
  PaperProps: {
    style: { maxHeight: SELECT_ITEM_HEIGHT * 6.5 },
  },
};

const truncateTagName = (name: string): string =>
  name.length > TAG_NAME_MAX_LENGTH
    ? `${name.slice(0, TAG_NAME_MAX_LENGTH)}…`
    : name;

// Shows a disabled placeholder row instead of an empty, unexplained panel.
const renderSelectOptions = <T,>(
  options: T[],
  getKey: (option: T) => string | number,
  getLabel: (option: T) => string,
  emptyLabel: string
) =>
  options.length === 0 ? (
    <MenuItem value="" disabled>
      {emptyLabel}
    </MenuItem>
  ) : (
    options.map((option) => (
      <MenuItem key={getKey(option)} value={getKey(option)}>
        {getLabel(option)}
      </MenuItem>
    ))
  );

interface ISubscriptionMappingProps {
  modelName: string;
  instanceName: string;
  isReadOnly: boolean;
  lockMessage: string | null;
  // True once this subscription exists on the server - its tag is then locked.
  isExistingSubscription: boolean;
  subscriptionScenarioTagId: number | '';
  subscriptionStatus: 'Active' | 'Inactive';
  scenarioLinkTags: IModelOption[];
  imConnections: IIMConnection[];
  availableModelInputs: IModelInputOption[];
  models: IModelOption[];
  sourceModelInstances: ISourceModelInstanceOption[];
  onSubscriptionScenarioTagChange: (value: number | '') => void;
  onSubscriptionStatusChange: (value: string) => void;
  onConnectionChange: (
    imConnectionId: string,
    updates: Partial<IIMConnection>
  ) => void;
  onAddConnection: (connection: IIMConnection) => void;
  onRemoveConnection: (imConnectionId: string) => void;
  onConnectionEditorOpenChange: (isOpen: boolean) => void;
  onBackToList: () => void;
  isSaving?: boolean;
}

// Ensures a previously-saved attribute value always has a matching
// MenuItem, even if it's no longer part of the live fetched list. Excludes
// DIMENSION_NAME_ATTRIBUTE, which is always rendered separately (CLD-3019).
const withSavedAttribute = (
  attributeOptions: string[],
  savedAttribute: string
): string[] =>
  savedAttribute &&
  savedAttribute !== DIMENSION_NAME_ATTRIBUTE &&
  !attributeOptions.includes(savedAttribute)
    ? [...attributeOptions, savedAttribute]
    : attributeOptions;

const isRealAttribute = (attribute: string): boolean =>
  !!attribute && !isNameAttribute(attribute);

// The worker accepts at most one side naming a real attribute; the other must
// be "Name" or omitted.
const isInvalidAdjustment = (adj: {
  sourceDimAttribute: string;
  targetDimAttribute: string;
}): boolean =>
  isRealAttribute(adj.sourceDimAttribute) &&
  isRealAttribute(adj.targetDimAttribute);

const SubscriptionMapping: FunctionComponent<ISubscriptionMappingProps> = ({
  modelName,
  instanceName,
  isReadOnly,
  lockMessage,
  isExistingSubscription,
  subscriptionScenarioTagId,
  subscriptionStatus,
  scenarioLinkTags,
  imConnections,
  availableModelInputs,
  models,
  sourceModelInstances,
  onSubscriptionScenarioTagChange,
  onSubscriptionStatusChange,
  onConnectionChange,
  onAddConnection,
  onRemoveConnection,
  onConnectionEditorOpenChange,
  onBackToList,
  isSaving,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  // Starts in edit mode for a brand-new subscription (nothing to show yet).
  const [isHeaderEditing, setIsHeaderEditing] = useState(
    !subscriptionScenarioTagId
  );

  // Locked once saved, or once a connection has been built against the tag.
  const isScenarioTagLocked =
    isExistingSubscription || imConnections.length > 0;

  // The connection currently open in the detail form (null = table only).
  const [editingConnection, setEditingConnection] =
    useState<IIMConnection | null>(null);
  const [isNewConnection, setIsNewConnection] = useState(false);

  // Disables the outer dialog's Save/Cancel/X while a connection edit is open.
  useEffect(() => {
    onConnectionEditorOpenChange(!!editingConnection);
    return () => onConnectionEditorOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onConnectionEditorOpenChange is caller-supplied and may be unstable; depending on it would re-fire the cleanup every render
  }, [editingConnection]);

  // Only surfaced once the user tries to save.
  const [connectionNameError, setConnectionNameError] = useState(false);

  // Dimensions mapped to the source Output the connection's tag resolves to.
  const [sourceOutputDimensions, setSourceOutputDimensions] = useState<
    IDimensionOption[]
  >([]);

  // Which "instanceId:outputTagId" sourceOutputDimensions was last loaded
  // for - avoids a flash of stale source dims against fresh target dims
  // while the (async) source fetch is still in flight.
  const [loadedSourceDimensionsKey, setLoadedSourceDimensionsKey] = useState<
    string | null
  >(null);

  // True when the last source-dimension fetch failed (cleared on success).
  const [sourceDimensionsError, setSourceDimensionsError] = useState(false);

  const sourceModelInstanceId = editingConnection?.sourceModelInstanceId;
  const outputTagId = editingConnection?.outputTagId;
  const sourceDimensionsKey =
    sourceModelInstanceId && outputTagId
      ? `${sourceModelInstanceId}:${outputTagId}`
      : null;
  const isLoadingSourceOutputDimensions =
    sourceDimensionsKey !== loadedSourceDimensionsKey;

  useEffect(() => {
    if (!sourceModelInstanceId || !outputTagId) {
      setSourceOutputDimensions([]);
      setSourceDimensionsError(false);
      setLoadedSourceDimensionsKey(null);
      return;
    }
    let ignore = false;
    const key = `${sourceModelInstanceId}:${outputTagId}`;
    API.load(
      `/instances/${sourceModelInstanceId}/imdl_output_dimensions/${outputTagId}`
    )
      .then((response) => {
        if (!ignore) {
          setSourceOutputDimensions(response?.items || []);
          setSourceDimensionsError(false);
        }
      })
      .catch(() => {
        if (!ignore) {
          // A failed fetch is not "no dimensions": flag it so the save is
          // blocked rather than prompting a bogus dimension-mismatch confirm.
          setSourceOutputDimensions([]);
          setSourceDimensionsError(true);
        }
      })
      .finally(() => {
        if (!ignore) setLoadedSourceDimensionsKey(key);
      });
    return () => {
      ignore = true;
    };
  }, [sourceModelInstanceId, outputTagId]);

  // CLD-3093: the API returns dimensions in each model's hierarchy order; keep it.
  const targetInputDimensions =
    availableModelInputs.find(
      (input) => input.id === editingConnection?.targetModelInputId
    )?.dimensions || [];

  const targetInputDimensionNames = targetInputDimensions.map(
    (dim) => dim.name
  );
  const sourceOutputDimensionNames = sourceOutputDimensions.map(
    (dim) => dim.name
  );

  // Target dimensions with no same-named source dimension: the only ones "*"
  // is offered for (the worker refuses it on a dimension the source has).
  const targetOnlyDimensionNames = targetInputDimensionNames.filter(
    (name) => !sourceOutputDimensionNames.includes(name)
  );

  // A dimension an adjustment row already resolves is not a mismatch, so it
  // shows green and does not trigger the "Save anyway?" prompt (CLD-3055).
  const completeAdjustments = (
    editingConnection?.dimMappingAdjustments || []
  ).filter(
    (adj) =>
      adj.targetDimension &&
      (isAllMembersAdjustment(adj) || adj.sourceDimension)
  );
  const coveredTargetDimensions = new Set(
    completeAdjustments.map((adj) => adj.targetDimension)
  );
  const coveredSourceDimensions = new Set(
    completeAdjustments
      .filter((adj) => !isAllMembersAdjustment(adj))
      .map((adj) => adj.sourceDimension)
  );

  const dimensionMismatch =
    editingConnection?.targetModelInputId && editingConnection?.outputTagId
      ? {
          sourceOnly: sourceOutputDimensionNames.filter(
            (name) =>
              !targetInputDimensionNames.includes(name) &&
              !coveredSourceDimensions.has(name)
          ),
          targetOnly: targetOnlyDimensionNames.filter(
            (name) => !coveredTargetDimensions.has(name)
          ),
        }
      : null;

  // Looked up from the already-fetched dimension lists - no extra API call.
  const attributesForSourceDimension = (dimensionName: string): string[] =>
    sourceOutputDimensions.find((dim) => dim.name === dimensionName)
      ?.attributes || [];
  const attributesForTargetDimension = (dimensionName: string): string[] =>
    targetInputDimensions.find((dim) => dim.name === dimensionName)
      ?.attributes || [];

  const selectedTagName =
    scenarioLinkTags.find((tag) => tag.id === subscriptionScenarioTagId)
      ?.Name || '';

  const getModelName = (modelId: number | ''): string =>
    models.find((m) => m.id === modelId)?.Name || '';

  const getInstanceName = (
    modelId: number | '',
    instanceId: number | ''
  ): string =>
    sourceModelInstances.find(
      (instance) =>
        instance.modelId === modelId && instance.modelInstanceId === instanceId
    )?.modelInstanceName || '';

  const getInputName = (inputId: number | ''): string =>
    availableModelInputs.find((input) => input.id === inputId)?.name || '';

  // outputTagName isn't persisted - always re-resolved from Model Inputs.
  const getOutputTagName = (targetModelInputId: number | ''): string =>
    availableModelInputs.find((input) => input.id === targetModelInputId)
      ?.outputTagName || '';

  const handleStartAddConnection = () => {
    setEditingConnection(blankConnection());
    setIsNewConnection(true);
    setConnectionNameError(false);
  };

  const handleStartEditConnection = (connection: IIMConnection) => {
    setEditingConnection({ ...connection });
    setIsNewConnection(false);
    setConnectionNameError(false);
  };

  const handleCancelConnectionEdit = () => {
    setEditingConnection(null);
    setIsNewConnection(false);
    setConnectionNameError(false);
  };

  const handleDeleteConnection = (connection: IIMConnection) => {
    confirm({
      title: 'Delete Connection',
      description: `Delete connection "${
        connection.imConnectionName || 'Unnamed'
      }"? This takes effect once you save the subscription.`,
      confirmationText: 'Delete',
      confirmationButtonProps: { color: 'error' },
    })
      .then(() => onRemoveConnection(connection.imConnectionId))
      .catch(() => {
        // Cancelled - leave the connection as-is.
      });
  };

  const handleCopyConnection = (connection: IIMConnection) => {
    onAddConnection({
      ...connection,
      imConnectionId: generateConnectionId(),
      dimMappings: connection.dimMappings.map((mapping) => ({ ...mapping })),
      dimMappingAdjustments: connection.dimMappingAdjustments.map(
        (adjustment) => ({ ...adjustment })
      ),
    });
    enqueueSnackbar('Connection copied.', { variant: 'success' });
  };

  const commitConnectionSave = () => {
    if (!editingConnection) return;
    if (isNewConnection) {
      onAddConnection(editingConnection);
    } else {
      onConnectionChange(editingConnection.imConnectionId, editingConnection);
    }
    setEditingConnection(null);
    setIsNewConnection(false);
  };

  // Save is blocked while any adjustment names a real attribute on both sides
  // (the worker rejects it; may come from an older saved subscription).
  const hasInvalidAdjustment =
    !!editingConnection?.dimMappingAdjustments.some(isInvalidAdjustment);

  const handleSaveConnectionEdit = () => {
    if (!editingConnection || hasInvalidAdjustment) return;
    if (!editingConnection.imConnectionName.trim()) {
      setConnectionNameError(true);
      enqueueSnackbar('Connection Name is required', { variant: 'error' });
      return;
    }
    setConnectionNameError(false);
    if (
      !editingConnection.sourceModelId ||
      !editingConnection.sourceModelInstanceId ||
      !editingConnection.targetModelInputId
    ) {
      enqueueSnackbar(
        'Source Model, Source Model Instance, and Model Input are required',
        { variant: 'error' }
      );
      return;
    }

    // CLD-3022: block saving an incomplete adjustment row.
    const hasIncompleteAdjustment =
      editingConnection.dimMappingAdjustments.some(
        (adj) =>
          !adj.targetDimension ||
          (!isAllMembersAdjustment(adj) && !adj.sourceDimension)
      );
    if (hasIncompleteAdjustment) {
      enqueueSnackbar(
        'Each Dim Mapping Adjustment needs a Target Dimension and either a Source Dimension or "*" - finish or remove any incomplete rows before saving.',
        { variant: 'error' }
      );
      return;
    }

    // "*" fills its Target Dimension on its own, so no other row may also
    // target it (the API refuses two rules for one target dimension).
    const allMembersTargets = editingConnection.dimMappingAdjustments
      .filter(isAllMembersAdjustment)
      .map((adj) => adj.targetDimension);
    const hasAllMembersConflict = allMembersTargets.some(
      (target) =>
        editingConnection.dimMappingAdjustments.filter(
          (adj) => adj.targetDimension === target
        ).length > 1
    );
    if (hasAllMembersConflict) {
      enqueueSnackbar(
        'A Target Dimension set to "*" cannot have another Dim Mapping Adjustment - remove the extra row before saving.',
        { variant: 'error' }
      );
      return;
    }

    // CLD-3020: block a duplicate Target/Source Dimension pair (attributes
    // not included in the key for now).
    const adjustmentKey = (adj: IDimMappingAdjustment): string =>
      [adj.targetDimension, adj.sourceDimension].join('|');
    const seenAdjustmentKeys = new Set<string>();
    const hasDuplicateAdjustment = editingConnection.dimMappingAdjustments.some(
      (adj) => {
        const key = adjustmentKey(adj);
        if (seenAdjustmentKeys.has(key)) return true;
        seenAdjustmentKeys.add(key);
        return false;
      }
    );
    if (hasDuplicateAdjustment) {
      enqueueSnackbar(
        'This connection maps the same Target Dimension to the same Source Dimension more than once - remove the duplicate before saving.',
        { variant: 'error' }
      );
      return;
    }

    // Fail closed: source dimensions could not be loaded, so the mismatch
    // check cannot be trusted - save nothing on unverified data.
    if (sourceDimensionsError) {
      enqueueSnackbar(
        "Could not load the source model's dimensions. Save is blocked until they load.",
        { variant: 'error' }
      );
      return;
    }

    const hasDimensionMismatch = !!(
      dimensionMismatch &&
      (dimensionMismatch.sourceOnly.length > 0 ||
        dimensionMismatch.targetOnly.length > 0)
    );
    if (hasDimensionMismatch) {
      confirm({
        title: 'Dimension Mismatch',
        description:
          "Source and target dimensions don't fully match for this connection. A Dim Mapping Adjustment may be needed, or the data may not load through correctly. Save anyway?",
        confirmationText: 'Save Anyway',
      })
        .then(() => commitConnectionSave())
        .catch(() => {
          // Cancelled - stay in the editor so the user can add an adjustment.
        });
      return;
    }

    commitConnectionSave();
  };

  const handleTargetInputChange = (inputId: number) => {
    if (!editingConnection) return;
    const input = availableModelInputs.find((i) => i.id === inputId);
    setEditingConnection({
      ...editingConnection,
      targetModelInputId: inputId,
      outputTagId: input?.outputTagId ?? null,
      outputTagName: input?.outputTagName ?? null,
    });
  };

  const handleAddAdjustment = () => {
    if (!editingConnection) return;
    setEditingConnection({
      ...editingConnection,
      dimMappingAdjustments: [
        ...editingConnection.dimMappingAdjustments,
        {
          sourceDimension: '',
          sourceDimAttribute: '',
          targetDimension: '',
          targetDimAttribute: '',
          mappingRule: 'exact_match',
        },
      ],
    });
  };

  const handleAdjustmentFieldChange = (
    index: number,
    field:
      | 'sourceDimension'
      | 'sourceDimAttribute'
      | 'targetDimension'
      | 'targetDimAttribute'
      | 'mappingRule',
    value: string
  ) => {
    if (!editingConnection) return;
    const adjustments = editingConnection.dimMappingAdjustments.map(
      (adj, i) => {
        if (i !== index) return adj;
        // Changing the dimension invalidates its previously-picked attribute.
        if (field === 'sourceDimension') {
          return { ...adj, sourceDimension: value, sourceDimAttribute: '' };
        }
        if (field === 'targetDimension') {
          return {
            ...adj,
            targetDimension: value,
            targetDimAttribute: '',
            mappingRule: EXACT_MATCH_RULE,
          };
        }
        // "*" takes no source side; picking any other attribute restores the
        // ordinary rule (CLD-3055).
        if (field === 'targetDimAttribute') {
          return value === ALL_MEMBERS_ATTRIBUTE
            ? {
                ...adj,
                targetDimAttribute: value,
                sourceDimension: '',
                sourceDimAttribute: '',
                mappingRule: ALL_MEMBERS_RULE,
              }
            : {
                ...adj,
                targetDimAttribute: value,
                mappingRule: EXACT_MATCH_RULE,
              };
        }
        return { ...adj, [field]: value };
      }
    );
    setEditingConnection({
      ...editingConnection,
      dimMappingAdjustments: adjustments,
    });
  };

  const handleRemoveAdjustment = (index: number) => {
    if (!editingConnection) return;
    setEditingConnection({
      ...editingConnection,
      dimMappingAdjustments: editingConnection.dimMappingAdjustments.filter(
        (_, i) => i !== index
      ),
    });
  };

  const sourceInstancesForEditor = editingConnection?.sourceModelId
    ? sourceModelInstances
        .filter(
          (instance) => instance.modelId === editingConnection.sourceModelId
        )
        .map((instance) => ({
          id: instance.modelInstanceId,
          Name: instance.modelInstanceName,
        }))
    : [];

  // Each column has its own minWidth + flex: 1 (grows, never shrinks below
  // it). Actions (always last) stays fixed-width since it's icon-only.
  const connectionColumns: ColDef[] = [
    {
      headerName: 'Connection Name',
      headerTooltip: 'Connection Name',
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.data?.imConnectionName || '—',
      cellRenderer: OverflowTooltip,
    },
    {
      headerName: 'Source Model',
      headerTooltip: 'Source Model',
      minWidth: 130,
      flex: 1,
      valueGetter: (params) => getModelName(params.data?.sourceModelId),
      cellRenderer: OverflowTooltip,
    },
    {
      headerName: 'Source Model Instance',
      headerTooltip: 'Source Model Instance',
      minWidth: 190,
      flex: 1,
      valueGetter: (params) =>
        getInstanceName(
          params.data?.sourceModelId,
          params.data?.sourceModelInstanceId
        ),
      cellRenderer: OverflowTooltip,
    },
    {
      headerName: 'Target Model Input',
      headerTooltip: 'Target Model Input',
      minWidth: 170,
      flex: 1,
      valueGetter: (params) => getInputName(params.data?.targetModelInputId),
      cellRenderer: OverflowTooltip,
    },
    {
      headerName: 'Output Tag',
      headerTooltip: 'Output Tag',
      minWidth: 120,
      flex: 1,
      valueGetter: (params) =>
        getOutputTagName(params.data?.targetModelInputId),
      cellRenderer: OverflowTooltip,
    },
    {
      headerName: 'IM Enabled',
      headerTooltip: 'IM Enabled',
      minWidth: 120,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<IIMConnection>) => (
        <Switch
          size="small"
          checked={!!params.data?.imEnabled}
          disabled={isReadOnly}
          onChange={(e) =>
            params.data &&
            onConnectionChange(params.data.imConnectionId, {
              imEnabled: e.target.checked,
            })
          }
        />
      ),
    },
    {
      headerName: 'Actions',
      headerTooltip: 'Actions',
      minWidth: 150,
      maxWidth: 150,
      resizable: false,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<IIMConnection>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              disabled={isReadOnly}
              onClick={() =>
                params.data && handleStartEditConnection(params.data)
              }
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              disabled={isReadOnly}
              onClick={() => params.data && handleCopyConnection(params.data)}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              disabled={isReadOnly}
              onClick={() => params.data && handleDeleteConnection(params.data)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 1.5, fontSize: '0.8125rem' }}>
      {/* Header with Back Button, Model Info, and Subscription Details */}
      <Box sx={{ mb: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<ArrowBack fontSize="small" />}
            onClick={onBackToList}
            disabled={isSaving || !!editingConnection}
            sx={{ textTransform: 'none' }}
          >
            Back to Subscriptions
          </Button>
        </Box>

        {lockMessage && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            {lockMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.6875rem' }}
              >
                Model
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: '0.8125rem' }}
              >
                {modelName}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.6875rem' }}
              >
                Model Instance
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: '0.8125rem' }}
              >
                {instanceName}
              </Typography>
            </Box>
          </Box>

          {isReadOnly || !isHeaderEditing ? (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.6875rem' }}
                >
                  Subscription Scenario Tag
                </Typography>
                <OverflowTooltip
                  value={selectedTagName || '—'}
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: '0.8125rem', maxWidth: 220 }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.6875rem' }}
                >
                  Subscription Status
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: '0.8125rem' }}
                >
                  {subscriptionStatus}
                </Typography>
              </Box>
              {!isReadOnly && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => setIsHeaderEditing(true)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
              <Tooltip
                title={
                  isScenarioTagLocked
                    ? isExistingSubscription
                      ? 'The Subscription Scenario Tag cannot be changed once a subscription is saved.'
                      : 'The Subscription Scenario Tag is locked once a connection has been added.'
                    : ''
                }
              >
                <span>
                  {/* A plain Select's menu grows to fit every tag with no cap
                    and no way to filter - with a long tag list that meant a
                    dropdown nearly as tall as the screen. Autocomplete gives
                    us both a capped, scrollable listbox (~8 rows) and
                    type-to-filter search for free. */}
                  <Autocomplete
                    size="small"
                    disabled={isScenarioTagLocked}
                    sx={{
                      minWidth: 250,
                      maxWidth: 250,
                      '& .MuiInputBase-input': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    options={scenarioLinkTags}
                    getOptionLabel={(tag) => tag.Name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      scenarioLinkTags.find(
                        (tag) => tag.id === subscriptionScenarioTagId
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      onSubscriptionScenarioTagChange(
                        newValue ? newValue.id : ''
                      )
                    }
                    ListboxProps={{
                      style: {
                        maxHeight: SELECT_ITEM_HEIGHT * 6.5,
                        overflowY: 'auto',
                      },
                    }}
                    renderOption={(props, option) => (
                      <Typography component="li" {...props} noWrap>
                        <Tooltip
                          title={option.Name}
                          arrow
                          disableHoverListener={
                            option.Name.length <= TAG_NAME_MAX_LENGTH
                          }
                        >
                          <span>{truncateTagName(option.Name)}</span>
                        </Tooltip>
                      </Typography>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subscription Scenario Tag"
                        inputProps={{
                          ...params.inputProps,
                          title:
                            scenarioLinkTags.find(
                              (tag) => tag.id === subscriptionScenarioTagId
                            )?.Name || '',
                        }}
                      />
                    )}
                  />
                </span>
              </Tooltip>

              <FormControl
                size="small"
                sx={{ minWidth: 180, ...DENSE_FIELD_SX }}
              >
                <InputLabel>Subscription Status</InputLabel>
                <Select
                  value={subscriptionStatus}
                  onChange={(e) => onSubscriptionStatusChange(e.target.value)}
                  label="Subscription Status"
                  MenuProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Done">
                <IconButton
                  size="small"
                  disabled={!subscriptionScenarioTagId}
                  onClick={() => setIsHeaderEditing(false)}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* IM Connections */}
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
          >
            Inter-Model Connections
          </Typography>
          {!isReadOnly && !editingConnection && (
            <Tooltip
              title={
                isHeaderEditing || !subscriptionScenarioTagId
                  ? 'Select and confirm a Subscription Scenario Tag first.'
                  : ''
              }
            >
              <span>
                <Button
                  size="small"
                  startIcon={<Add fontSize="small" />}
                  disabled={isHeaderEditing || !subscriptionScenarioTagId}
                  onClick={handleStartAddConnection}
                >
                  Add Connection
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {!editingConnection && (
          // Our own scroll container (not ag-grid's) - see CONNECTIONS_TABLE_MIN_WIDTH.
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box sx={{ minWidth: CONNECTIONS_TABLE_MIN_WIDTH }}>
              <Table
                id="imdl-connections-table"
                isLoading={false}
                isSuccess
                pagination={imConnections.length > 0}
                rowCount={imConnections.length}
                rowData={imConnections}
                columnDefs={connectionColumns}
                defaultColDef={{
                  suppressAutoSize: true,
                  suppressMenu: true,
                  suppressSizeToFit: true,
                }}
              />
            </Box>
          </Box>
        )}

        {editingConnection && (
          <Card variant="outlined" sx={{ mt: 1.5 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  {isNewConnection ? 'New Connection' : 'Edit Connection'}
                </Typography>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  componentsProps={{
                    typography: {
                      variant: 'body2',
                      sx: { fontSize: '0.8125rem' },
                    },
                  }}
                  control={
                    <Switch
                      size="small"
                      checked={editingConnection.imEnabled}
                      disabled={isReadOnly}
                      onChange={(e) =>
                        setEditingConnection({
                          ...editingConnection,
                          imEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="IM Enabled"
                />
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    size="small"
                    fullWidth
                    required
                    sx={DENSE_FIELD_SX}
                    label="Connection Name"
                    disabled={isReadOnly}
                    error={connectionNameError}
                    value={editingConnection.imConnectionName}
                    inputProps={{ maxLength: CONNECTION_NAME_MAX_LENGTH }}
                    helperText={
                      connectionNameError
                        ? 'Connection Name is required'
                        : `${editingConnection.imConnectionName.length}/${CONNECTION_NAME_MAX_LENGTH}`
                    }
                    onChange={(e) => {
                      const value = e.target.value.slice(
                        0,
                        CONNECTION_NAME_MAX_LENGTH
                      );
                      if (value.trim()) {
                        setConnectionNameError(false);
                      }
                      setEditingConnection({
                        ...editingConnection,
                        imConnectionName: value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    size="small"
                    fullWidth
                    sx={DENSE_FIELD_SX}
                    disabled={isReadOnly || !subscriptionScenarioTagId}
                  >
                    <InputLabel>Source Model</InputLabel>
                    <Select
                      value={editingConnection.sourceModelId}
                      label="Source Model"
                      MenuProps={SELECT_MENU_PROPS}
                      onChange={(e) =>
                        setEditingConnection({
                          ...editingConnection,
                          sourceModelId: e.target.value as number,
                          sourceModelInstanceId: '',
                        })
                      }
                    >
                      {renderSelectOptions(
                        models,
                        (model) => model.id,
                        (model) => model.Name,
                        'No Source Models available'
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    size="small"
                    fullWidth
                    sx={DENSE_FIELD_SX}
                    disabled={isReadOnly || !editingConnection.sourceModelId}
                  >
                    <InputLabel>Source Model Instance</InputLabel>
                    <Select
                      value={editingConnection.sourceModelInstanceId}
                      label="Source Model Instance"
                      MenuProps={SELECT_MENU_PROPS}
                      onChange={(e) =>
                        setEditingConnection({
                          ...editingConnection,
                          sourceModelInstanceId: e.target.value as number,
                        })
                      }
                    >
                      {renderSelectOptions(
                        sourceInstancesForEditor,
                        (instance) => instance.id,
                        (instance) => instance.Name,
                        'No Source Model Instances available'
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    size="small"
                    fullWidth
                    sx={DENSE_FIELD_SX}
                    disabled={isReadOnly}
                  >
                    <InputLabel>Target Model Input</InputLabel>
                    <Select
                      value={editingConnection.targetModelInputId}
                      label="Target Model Input"
                      MenuProps={SELECT_MENU_PROPS}
                      onChange={(e) =>
                        handleTargetInputChange(e.target.value as number)
                      }
                    >
                      {renderSelectOptions(
                        availableModelInputs,
                        (input) => input.id,
                        (input) => input.name,
                        'No Target Model Inputs available'
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    size="small"
                    fullWidth
                    sx={DENSE_FIELD_SX}
                    label="Output Tag"
                    value={getOutputTagName(
                      editingConnection.targetModelInputId
                    )}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.25 }} />

              {/* Dim Mappings - read-only, driven by the target Model Input
                  (InputDimensionMapping) and, once an output tag is
                  resolved, the source Output it bridges to
                  (OutputDimensionMapping). Any dimension only on one side
                  is highlighted so the user knows to add a Dim Mapping
                  Adjustment before saving, or the data won't load through. */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  mb: 1.25,
                  borderRadius: 1.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <CompareArrows sx={{ fontSize: 16 }} color="action" />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
                  >
                    Dimension Mapping
                  </Typography>
                  <Tooltip title="Dimensions come from the target Model Input and the source output tied to its output tag. Matching names load automatically; a dimension highlighted in orange on either side has no match on the other side and needs a Dim Mapping Adjustment below.">
                    <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>

                {sourceDimensionsError && !isLoadingSourceOutputDimensions && (
                  <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
                    Could not load the source model&apos;s dimensions.
                  </Alert>
                )}

                {isLoadingSourceOutputDimensions ? (
                  // Waits for the async source fetch so both render together.
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Loading dimensions…
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.6875rem' }}
                      >
                        Target - Model Input
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {!editingConnection.targetModelInputId ? (
                          <Typography variant="caption" color="text.secondary">
                            Select a Target Model Input first.
                          </Typography>
                        ) : targetInputDimensions.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No dimensions mapped to this input.
                          </Typography>
                        ) : (
                          targetInputDimensions.map((dim) => {
                            const isMismatched =
                              dimensionMismatch?.targetOnly.includes(dim.name);
                            return (
                              <Chip
                                key={dim.name}
                                label={dim.name}
                                size="small"
                                variant="outlined"
                                color={isMismatched ? 'warning' : 'success'}
                                sx={{ fontSize: '0.6875rem', height: 22 }}
                              />
                            );
                          })
                        )}
                      </Box>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 1.5, display: { xs: 'none', sm: 'block' } }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.6875rem' }}
                      >
                        Source - Model Output
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {!editingConnection.outputTagId ? (
                          <Typography variant="caption" color="text.secondary">
                            Select a Target Model Input to resolve the output
                            tag.
                          </Typography>
                        ) : sourceOutputDimensions.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No dimensions mapped to this output tag on the
                            source instance.
                          </Typography>
                        ) : (
                          sourceOutputDimensions.map((dim) => {
                            const isMismatched =
                              dimensionMismatch?.sourceOnly.includes(dim.name);
                            return (
                              <Chip
                                key={dim.name}
                                label={dim.name}
                                size="small"
                                variant="outlined"
                                color={isMismatched ? 'warning' : 'success'}
                                sx={{ fontSize: '0.6875rem', height: 22 }}
                              />
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Dim Mapping Adjustments */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.6875rem' }}
                >
                  Dim Mapping Adjustments
                </Typography>
                <Tooltip title='For dimensions that do not share a name across source and target, map Source Dimension (+ optional attribute) to Target Dimension (+ optional attribute). Attribute options are scoped to whichever dimension is selected. For a Target Dimension the source does not have, pick "*" to load each value into every matching row, whatever that dimension holds.'>
                  <Info sx={{ fontSize: 13, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <Box sx={{ mt: 0.5, mb: 0.75 }}>
                {editingConnection.dimMappingAdjustments.map((adj, i) => {
                  // Filters out "Name" (its own fixed menu item elsewhere)
                  // and keeps a saved-but-no-longer-fetched attribute in the
                  // list so a prior selection still shows on reopen.
                  const sourceAttributeOptions = withSavedAttribute(
                    attributesForSourceDimension(adj.sourceDimension).filter(
                      (attributeName) => !isNameAttribute(attributeName)
                    ),
                    adj.sourceDimAttribute
                  );
                  const isAllMembers = isAllMembersAdjustment(adj);
                  const targetAttributeOptions = withSavedAttribute(
                    attributesForTargetDimension(adj.targetDimension).filter(
                      (attributeName) => !isNameAttribute(attributeName)
                    ),
                    isAllMembers ? '' : adj.targetDimAttribute
                  );
                  // "*" only makes sense where the source has no such
                  // dimension; kept visible on a row already saved with it.
                  const canUseAllMembers =
                    isAllMembers ||
                    targetOnlyDimensionNames.includes(adj.targetDimension);
                  return (
                    <Box key={i}>
                      {/* CLD-3021: wider gaps + a divider between rows so
                        labels don't crowd each other. */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          rowGap: 1.5,
                          py: 1,
                          alignItems: 'center',
                        }}
                      >
                        <FormControl
                          size="small"
                          sx={{ width: 160, ...DENSE_FIELD_SX }}
                          disabled={isReadOnly}
                        >
                          <InputLabel shrink>Target Dimension</InputLabel>
                          <Select
                            label="Target Dimension"
                            notched
                            MenuProps={SELECT_MENU_PROPS}
                            value={adj.targetDimension}
                            onChange={(e) =>
                              handleAdjustmentFieldChange(
                                i,
                                'targetDimension',
                                e.target.value as string
                              )
                            }
                          >
                            {renderSelectOptions(
                              targetInputDimensions,
                              (dim) => dim.name,
                              (dim) => dim.name,
                              'No Target Dimensions available'
                            )}
                          </Select>
                        </FormControl>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          .
                        </Typography>
                        <FormControl
                          size="small"
                          sx={{ width: 140, ...DENSE_FIELD_SX }}
                          disabled={isReadOnly || !adj.targetDimension}
                        >
                          <InputLabel shrink>Attribute</InputLabel>
                          <Select
                            label="Attribute"
                            notched
                            displayEmpty
                            MenuProps={SELECT_MENU_PROPS}
                            value={adj.targetDimAttribute}
                            onChange={(e) =>
                              handleAdjustmentFieldChange(
                                i,
                                'targetDimAttribute',
                                e.target.value as string
                              )
                            }
                          >
                            <MenuItem value={DIMENSION_NAME_ATTRIBUTE}>
                              {DIMENSION_NAME_ATTRIBUTE}
                            </MenuItem>
                            {canUseAllMembers && (
                              <MenuItem value={ALL_MEMBERS_ATTRIBUTE}>
                                {ALL_MEMBERS_ATTRIBUTE} (all members)
                              </MenuItem>
                            )}
                            {targetAttributeOptions.map((attributeName) => (
                              <MenuItem
                                key={attributeName}
                                value={attributeName}
                                disabled={
                                  isRealAttribute(adj.sourceDimAttribute) &&
                                  attributeName !== adj.targetDimAttribute
                                }
                              >
                                {attributeName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          =
                        </Typography>

                        <FormControl
                          size="small"
                          sx={{ width: 160, ...DENSE_FIELD_SX }}
                          disabled={isReadOnly || isAllMembers}
                        >
                          <InputLabel shrink>Source Dimension</InputLabel>
                          <Select
                            label="Source Dimension"
                            notched
                            MenuProps={SELECT_MENU_PROPS}
                            value={adj.sourceDimension}
                            onChange={(e) =>
                              handleAdjustmentFieldChange(
                                i,
                                'sourceDimension',
                                e.target.value as string
                              )
                            }
                          >
                            {renderSelectOptions(
                              sourceOutputDimensions,
                              (dim) => dim.name,
                              (dim) => dim.name,
                              'No Source Dimensions available'
                            )}
                          </Select>
                        </FormControl>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          .
                        </Typography>
                        <FormControl
                          size="small"
                          sx={{ width: 140, ...DENSE_FIELD_SX }}
                          disabled={
                            isReadOnly || isAllMembers || !adj.sourceDimension
                          }
                        >
                          <InputLabel shrink>Attribute</InputLabel>
                          <Select
                            label="Attribute"
                            notched
                            displayEmpty
                            MenuProps={SELECT_MENU_PROPS}
                            value={adj.sourceDimAttribute}
                            onChange={(e) =>
                              handleAdjustmentFieldChange(
                                i,
                                'sourceDimAttribute',
                                e.target.value as string
                              )
                            }
                          >
                            <MenuItem value={DIMENSION_NAME_ATTRIBUTE}>
                              {DIMENSION_NAME_ATTRIBUTE}
                            </MenuItem>
                            {sourceAttributeOptions.map((attributeName) => (
                              <MenuItem
                                key={attributeName}
                                value={attributeName}
                                disabled={
                                  isRealAttribute(adj.targetDimAttribute) &&
                                  attributeName !== adj.sourceDimAttribute
                                }
                              >
                                {attributeName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {!isReadOnly && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAdjustment(i)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                        {isInvalidAdjustment(adj) && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ flexBasis: '100%' }}
                          >
                            Only one side may use an attribute other than "
                            {DIMENSION_NAME_ATTRIBUTE}". Set the other side to "
                            {DIMENSION_NAME_ATTRIBUTE}".
                          </Typography>
                        )}
                      </Box>
                      {i <
                        editingConnection.dimMappingAdjustments.length - 1 && (
                        <Divider sx={{ mb: 1 }} />
                      )}
                    </Box>
                  );
                })}
                {!isReadOnly && (
                  <Button
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={handleAddAdjustment}
                  >
                    Add Adjustment
                  </Button>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                  mt: 1.25,
                }}
              >
                <Button size="small" onClick={handleCancelConnectionEdit}>
                  Cancel
                </Button>
                {!isReadOnly && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveConnectionEdit}
                    disabled={hasInvalidAdjustment}
                  >
                    Save Connection
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionMapping;
